'use client';

import { useEffect, useState } from 'react';
import {
  MOODS,
  moodFromKeyword,
  recipeFromSeed,
  makeRng,
  buildAiPalette,
  readableTextColor,
  type PaletteRecipe,
} from '@/lib/browser-palette-ai-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type Slot = { hex: string; locked: boolean };
type Mode = 'mood' | 'seed';

export default function BrowserAiColorPaletteGenerator() {
  const [mode, setMode] = useState<Mode>('mood');
  const [keyword, setKeyword] = useState('sunset');
  const [seed, setSeed] = useState('#2563EB');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function currentRecipe(): PaletteRecipe | null {
    if (mode === 'seed') {
      return recipeFromSeed(seed);
    }
    return moodFromKeyword(keyword).recipe;
  }

  // Roll a fresh palette, keeping any locked colors in place.
  function regenerate(reset = false) {
    if (typeof window === 'undefined') return;
    const recipe = currentRecipe();
    if (!recipe) {
      setError('Enter a valid 6-digit hex color (for example #2563EB).');
      return;
    }
    setError(null);
    const rng = makeRng((Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0);
    const fresh = buildAiPalette(recipe, rng);
    setSlots((prev) => {
      if (reset || prev.length !== 5) {
        return fresh.map((hex) => ({ hex, locked: false }));
      }
      return prev.map((slot, i) => (slot.locked ? slot : { hex: fresh[i], locked: false }));
    });
  }

  // Seed the first palette after mount so the server render stays deterministic.
  useEffect(() => {
    regenerate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleLock(index: number) {
    setSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, locked: !slot.locked } : slot)),
    );
  }

  async function handleCopy(label: string, value: string) {
    try {
      await copyText(value);
      setCopyMessage(`${label} copied to clipboard.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  const paletteString = slots.map((slot) => slot.hex).join(', ');

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Color</span>
        <h2 className="tool-live-title">AI color palette generator</h2>
        <p className="tool-live-subtitle">
          Generate cohesive 5-color palettes from a mood keyword or a seed color. Lock the colors
          you like and regenerate the rest. All math runs locally, no model or server involved.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Start from</span>
              <select onChange={(e) => setMode(e.target.value as Mode)} value={mode}>
                <option value="mood">Mood or keyword</option>
                <option value="seed">Seed color</option>
              </select>
            </label>

            {mode === 'mood' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>Mood or keyword</span>
                <input
                  list="ai-palette-moods"
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="sunset, ocean, forest, pastel, neon..."
                  type="text"
                  value={keyword}
                />
                <datalist id="ai-palette-moods">
                  {MOODS.map((mood) => (
                    <option key={mood.key} value={mood.label.toLowerCase()} />
                  ))}
                </datalist>
              </label>
            ) : (
              <>
                <label className="tool-live-field">
                  <span>Seed color</span>
                  <input
                    aria-label="Seed color picker"
                    onChange={(e) => setSeed(e.target.value.toUpperCase())}
                    style={{
                      width: '100%',
                      height: '3rem',
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(seed) ? seed : '#2563EB'}
                  />
                </label>
                <label className="tool-live-field">
                  <span>HEX value</span>
                  <input
                    onChange={(e) => {
                      const next = e.target.value.trim();
                      setSeed(next.startsWith('#') ? next.toUpperCase() : `#${next.toUpperCase()}`);
                    }}
                    placeholder="#2563EB"
                    type="text"
                    value={seed}
                  />
                </label>
              </>
            )}
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => regenerate(false)}
              type="button"
            >
              Regenerate
            </button>
            <button
              className="button button-secondary"
              onClick={() => regenerate(true)}
              type="button"
            >
              New palette
            </button>
            <button
              className="button button-secondary"
              disabled={slots.length === 0}
              onClick={() => handleCopy('Palette', paletteString)}
              type="button"
            >
              Copy all
            </button>
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          <p className="tool-live-panel-note">
            Regenerate keeps locked swatches and re-rolls the rest. New palette clears all locks.
          </p>
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '0.4rem',
            }}
          >
            {slots.map((slot, index) => {
              const text = readableTextColor(slot.hex);
              return (
                <div key={`${index}-${slot.hex}`} style={{ display: 'grid', gap: '0.3rem' }}>
                  <button
                    onClick={() => handleCopy(slot.hex, slot.hex)}
                    style={{
                      background: slot.hex,
                      color: text,
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: '8px',
                      height: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      padding: '0.35rem',
                      cursor: 'pointer',
                    }}
                    title={`Copy ${slot.hex}`}
                    type="button"
                  >
                    {slot.hex}
                  </button>
                  <button
                    aria-pressed={slot.locked}
                    className="button button-secondary"
                    onClick={() => toggleLock(index)}
                    style={{ padding: '0.25rem 0.4rem', fontSize: '0.7rem' }}
                    title={slot.locked ? 'Unlock this color' : 'Lock this color'}
                    type="button"
                  >
                    {slot.locked ? 'Locked' : 'Lock'}
                  </button>
                </div>
              );
            })}
          </div>
          {slots.length === 0 ? (
            <p className="tool-live-panel-note">Your palette will appear here.</p>
          ) : (
            <p className="tool-live-panel-note">
              Tap a swatch to copy its hex. Generated locally in your browser.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
